'use strict'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useHistory, useLocation } from 'react-router-dom'
import {
  makeStyles,
  Paper,
  Toolbar,
  Typography,
  Box,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  TextField,
  Checkbox,
  ListItemText
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import RefreshIcon from '@material-ui/icons/Refresh'
import RoomIcon from '@material-ui/icons/Room'
import TimelineIcon from '@material-ui/icons/Timeline'
import { GoogleMap, LoadScript, Marker, Polyline, InfoWindow } from '@react-google-maps/api'

const mapContainerStyle = {
  width: '100%',
  height: '68vh'
}

const DEFAULT_CENTER = { lat: 33.4484, lng: -112.074 }

const CREW_COLORS = [
  '#E53935', '#1E88E5', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1', '#6D4C41', '#3949AB', '#00897B', '#F4511E',
  '#5E35B1', '#039BE5', '#7CB342', '#C0CA33', '#D81B60', '#546E7A', '#C2185B', '#2E7D32', '#1565C0', '#EF6C00'
]

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    padding: theme.spacing(2)
  },
  paper: {
    width: '100%'
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(2)
  },
  title: {
    marginRight: theme.spacing(2)
  },
  select: {
    minWidth: 220
  },
  selectWide: {
    minWidth: 300
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2, 2, 2)
  },
  mapWrap: {
    padding: theme.spacing(1.5)
  },
  detailPanel: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5)
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing(1)
  },
  detailLabel: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary
  },
  detailValue: {
    fontSize: '0.9rem',
    fontWeight: 600
  },
  markerLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}))

const formatDateTime = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString()
}

const getCrewColor = (index) => {
  if (!Number.isFinite(Number(index))) return '#9E9E9E'
  return CREW_COLORS[Math.abs(Number(index)) % CREW_COLORS.length]
}

function RoutingTerritoryMapView ({ url }) {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()

  const requestedScenarioId = useMemo(
    () => (new URLSearchParams(location.search).get('scenarioId') || '').trim(),
    [location.search]
  )

  const [scenarios, setScenarios] = useState([])
  const [loadingScenarios, setLoadingScenarios] = useState(true)

  const [useScenarioData, setUseScenarioData] = useState(true)
  const [selectedScenarioId, setSelectedScenarioId] = useState('')

  const [showVipOnly, setShowVipOnly] = useState(false)
  const [showLockedOnly, setShowLockedOnly] = useState(false)
  const [showAnchorOnly, setShowAnchorOnly] = useState(false)

  const [loadingMap, setLoadingMap] = useState(false)
  const [mapError, setMapError] = useState(null)
  const [featureCollection, setFeatureCollection] = useState({ type: 'FeatureCollection', features: [] })

  const [crewFilterIds, setCrewFilterIds] = useState([])
  const [selectedCrewId, setSelectedCrewId] = useState('')
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10))

  const [loadingRoute, setLoadingRoute] = useState(false)
  const [routeResponse, setRouteResponse] = useState(null)

  const [selectedProperty, setSelectedProperty] = useState(null)
  const [mapRef, setMapRef] = useState(null)

  const [infoWindowPropertyId, setInfoWindowPropertyId] = useState(null)

  const selectedScenario = useMemo(
    () => scenarios.find(item => item._id === selectedScenarioId) || null,
    [scenarios, selectedScenarioId]
  )

  const effectiveScenarioId = useScenarioData ? selectedScenarioId : ''

  const propertyFeatures = useMemo(() => {
    const features = featureCollection?.features || []
    return features.filter(item => item?.properties?.featureType === 'property')
  }, [featureCollection])

  const crewFeatures = useMemo(() => {
    const features = featureCollection?.features || []
    return features.filter(item => item?.properties?.featureType === 'crewCentroid')
  }, [featureCollection])

  const crews = useMemo(() => {
    return crewFeatures
      .map(item => ({
        crewId: String(item?.properties?.crewId || ''),
        crewName: item?.properties?.crewName || 'Unknown crew',
        crewColorIndex: item?.properties?.crewColorIndex
      }))
      .filter(item => item.crewId)
      .sort((a, b) => a.crewName.localeCompare(b.crewName))
  }, [crewFeatures])

  const filteredPropertyFeatures = useMemo(() => {
    const selectedCrewSet = new Set((crewFilterIds || []).map(String))
    const hasCrewFilter = selectedCrewSet.size > 0

    return propertyFeatures.filter(item => {
      const props = item.properties || {}
      if (showVipOnly && !props.isVip) return false
      if (showLockedOnly && !props.isLocked) return false
      if (showAnchorOnly && !props.isAnchor) return false
      if (hasCrewFilter && !selectedCrewSet.has(String(props.assignedCrewId || ''))) return false
      return true
    })
  }, [crewFilterIds, propertyFeatures, showAnchorOnly, showLockedOnly, showVipOnly])

  const filteredCrewFeatures = useMemo(() => {
    const selectedCrewSet = new Set((crewFilterIds || []).map(String))
    const hasCrewFilter = selectedCrewSet.size > 0
    if (!hasCrewFilter) return crewFeatures

    return crewFeatures.filter(item => selectedCrewSet.has(String(item?.properties?.crewId || '')))
  }, [crewFeatures, crewFilterIds])

  const mapCenter = useMemo(() => {
    if (filteredPropertyFeatures.length > 0) {
      const [lng, lat] = filteredPropertyFeatures[0]?.geometry?.coordinates || []
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng }
      }
    }

    if (filteredCrewFeatures.length > 0) {
      const [lng, lat] = filteredCrewFeatures[0]?.geometry?.coordinates || []
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng }
      }
    }

    return DEFAULT_CENTER
  }, [filteredCrewFeatures, filteredPropertyFeatures])

  const routePath = useMemo(() => {
    const coordinates = routeResponse?.route?.geometry?.coordinates || []
    return coordinates
      .map(item => {
        const [lng, lat] = item || []
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
        return { lat, lng }
      })
      .filter(Boolean)
  }, [routeResponse])

  const selectedPropertyForInfoWindow = useMemo(() => {
    if (!infoWindowPropertyId) return null
    return filteredPropertyFeatures.find(item => String(item?.properties?.aspirePropertyId || '') === String(infoWindowPropertyId || '')) || null
  }, [filteredPropertyFeatures, infoWindowPropertyId])

  const fetchScenarios = useCallback(async () => {
    setLoadingScenarios(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios`)
      const nextScenarios = response.data?.scenarios || []
      setScenarios(nextScenarios)

      if (nextScenarios.length === 0) {
        setSelectedScenarioId('')
        setUseScenarioData(false)
        return
      }

      if (requestedScenarioId && nextScenarios.some(item => item._id === requestedScenarioId)) {
        setSelectedScenarioId(requestedScenarioId)
        setUseScenarioData(true)
        return
      }

      setSelectedScenarioId(prev => {
        if (prev && nextScenarios.some(item => item._id === prev)) return prev
        const draft = nextScenarios.find(item => item.status === 'draft')
        return draft?._id || nextScenarios[0]?._id || ''
      })
    } catch (err) {
      console.error('Error loading scenarios for map view:', err)
      setScenarios([])
      setSelectedScenarioId('')
      setUseScenarioData(false)
    } finally {
      setLoadingScenarios(false)
    }
  }, [requestedScenarioId, url])

  const fetchMapData = useCallback(async () => {
    setLoadingMap(true)
    setMapError(null)
    try {
      const endpoint = effectiveScenarioId
        ? `${url}/api/hmi/territory/scenarios/${effectiveScenarioId}/map-data`
        : `${url}/api/hmi/territory/map-data`

      const response = await axios.get(endpoint)
      setFeatureCollection(response.data?.featureCollection || { type: 'FeatureCollection', features: [] })
    } catch (err) {
      console.error('Error loading territory map data:', err)
      setFeatureCollection({ type: 'FeatureCollection', features: [] })
      setMapError(err.response?.data?.message || err.message || 'Failed to load map data')
    } finally {
      setLoadingMap(false)
    }
  }, [effectiveScenarioId, url])

  const fetchCrewRoute = useCallback(async () => {
    if (!selectedCrewId || !selectedDate) {
      setRouteResponse(null)
      return
    }

    setLoadingRoute(true)
    try {
      const endpoint = effectiveScenarioId
        ? `${url}/api/hmi/territory/scenarios/${effectiveScenarioId}/crews/${selectedCrewId}/route`
        : `${url}/api/hmi/territory/crews/${selectedCrewId}/route`

      const response = await axios.get(endpoint, { params: { date: selectedDate } })
      setRouteResponse(response.data || null)
    } catch (err) {
      console.error('Error loading crew route visualization:', err)
      setRouteResponse(null)
    } finally {
      setLoadingRoute(false)
    }
  }, [effectiveScenarioId, selectedCrewId, selectedDate, url])

  useEffect(() => {
    fetchScenarios()
  }, [fetchScenarios])

  useEffect(() => {
    if (useScenarioData && !selectedScenarioId) return
    fetchMapData()
  }, [fetchMapData, selectedScenarioId, useScenarioData])

  useEffect(() => {
    if (!crews.length) {
      setSelectedCrewId('')
      return
    }

    if (selectedCrewId && crews.some(item => item.crewId === selectedCrewId)) {
      return
    }

    if (crewFilterIds.length === 1 && crews.some(item => item.crewId === crewFilterIds[0])) {
      setSelectedCrewId(crewFilterIds[0])
      return
    }

    setSelectedCrewId(crews[0].crewId)
  }, [crewFilterIds, crews, selectedCrewId])

  useEffect(() => {
    fetchCrewRoute()
  }, [fetchCrewRoute])

  useEffect(() => {
    if (!mapRef || !window.google || !window.google.maps) return

    const bounds = new window.google.maps.LatLngBounds()
    let pointCount = 0

    filteredPropertyFeatures.forEach(item => {
      const [lng, lat] = item?.geometry?.coordinates || []
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      bounds.extend({ lat, lng })
      pointCount += 1
    })

    if (pointCount === 0) {
      filteredCrewFeatures.forEach(item => {
        const [lng, lat] = item?.geometry?.coordinates || []
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
        bounds.extend({ lat, lng })
        pointCount += 1
      })
    }

    if (pointCount > 1) {
      mapRef.fitBounds(bounds)
    }
  }, [filteredCrewFeatures, filteredPropertyFeatures, mapRef])

  const mapMetaChips = useMemo(() => {
    const assignedCount = filteredPropertyFeatures.filter(item => !!item?.properties?.assignedCrewId).length
    const vipCount = filteredPropertyFeatures.filter(item => !!item?.properties?.isVip).length
    const lockCount = filteredPropertyFeatures.filter(item => !!item?.properties?.isLocked).length
    const anchorCount = filteredPropertyFeatures.filter(item => !!item?.properties?.isAnchor).length

    return {
      totalProperties: filteredPropertyFeatures.length,
      assignedCount,
      vipCount,
      lockCount,
      anchorCount
    }
  }, [filteredPropertyFeatures])

  const activeProperty = selectedProperty || selectedPropertyForInfoWindow

  const gmapKey = process.env.REACT_APP_GMAPKEY

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Toolbar className={classes.toolbar}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/routing/territory/dashboard')}
          >
            Back
          </Button>

          <Typography variant="h5" className={classes.title}>Territory Map</Typography>

          <FormControlLabel
            control={(
              <Switch
                color="secondary"
                checked={useScenarioData}
                onChange={(event) => setUseScenarioData(event.target.checked)}
              />
            )}
            label="Use scenario"
          />

          <Select
            className={classes.select}
            size="small"
            variant="outlined"
            value={selectedScenarioId}
            disabled={!useScenarioData || loadingScenarios || scenarios.length === 0}
            onChange={(event) => setSelectedScenarioId(event.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>Select scenario</MenuItem>
            {scenarios.map(item => (
              <MenuItem key={item._id} value={item._id}>{item.name} ({item.status})</MenuItem>
            ))}
          </Select>

          <Select
            className={classes.selectWide}
            size="small"
            variant="outlined"
            value={crewFilterIds}
            multiple
            displayEmpty
            renderValue={(selected) => {
              if (!selected || selected.length === 0) return 'All crews'
              const labelById = new Map(crews.map(item => [item.crewId, item.crewName]))
              return selected.map(id => labelById.get(id) || id).join(', ')
            }}
            onChange={(event) => {
              const value = event.target.value
              setCrewFilterIds(typeof value === 'string' ? value.split(',') : (value || []))
            }}
          >
            {crews.map(item => (
              <MenuItem key={item.crewId} value={item.crewId}>
                <Checkbox checked={crewFilterIds.indexOf(item.crewId) > -1} />
                <ListItemText primary={item.crewName} />
              </MenuItem>
            ))}
          </Select>

          <FormControlLabel
            control={<Switch checked={showVipOnly} onChange={(event) => setShowVipOnly(event.target.checked)} color="secondary" />}
            label="VIP"
          />
          <FormControlLabel
            control={<Switch checked={showLockedOnly} onChange={(event) => setShowLockedOnly(event.target.checked)} color="secondary" />}
            label="Locked"
          />
          <FormControlLabel
            control={<Switch checked={showAnchorOnly} onChange={(event) => setShowAnchorOnly(event.target.checked)} color="secondary" />}
            label="Anchor"
          />

          <Select
            className={classes.select}
            size="small"
            variant="outlined"
            value={selectedCrewId}
            onChange={(event) => setSelectedCrewId(event.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>Select route crew</MenuItem>
            {crews.map(item => (
              <MenuItem key={item.crewId} value={item.crewId}>{item.crewName}</MenuItem>
            ))}
          </Select>

          <TextField
            type="date"
            size="small"
            variant="outlined"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Button
            variant="outlined"
            size="small"
            startIcon={<TimelineIcon />}
            onClick={fetchCrewRoute}
            disabled={!selectedCrewId || loadingRoute}
          >
            {loadingRoute ? 'Loading Route...' : 'Refresh Route'}
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchMapData}
            disabled={loadingMap}
          >
            {loadingMap ? 'Loading Map...' : 'Refresh Map'}
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<RoomIcon />}
            onClick={() => history.push(effectiveScenarioId ? `/routing/territory?scenarioId=${effectiveScenarioId}` : '/routing/territory')}
          >
            Open Workbench
          </Button>
        </Toolbar>

        <div className={classes.chipsRow}>
          <Chip size="small" label={useScenarioData ? `Scenario: ${selectedScenario?.name || 'None'}` : 'Live (published)'} />
          <Chip size="small" label={`Properties: ${mapMetaChips.totalProperties}`} />
          <Chip size="small" label={`Assigned: ${mapMetaChips.assignedCount}`} />
          <Chip size="small" label={`VIP: ${mapMetaChips.vipCount}`} />
          <Chip size="small" label={`Locked: ${mapMetaChips.lockCount}`} />
          <Chip size="small" label={`Anchors: ${mapMetaChips.anchorCount}`} />
          <Chip size="small" label={`Centroids: ${filteredCrewFeatures.length}`} />
          <Chip size="small" label={`Route stops: ${routeResponse?.meta?.stopCount || 0}`} />
          <Chip size="small" label={`Route mode: ${routeResponse?.meta?.mode || '-'}`} />
          <Chip size="small" label={`Date: ${selectedDate}`} />
        </div>

        {mapError && (
          <Box px={2} pb={1}>
            <Typography color="error">{mapError}</Typography>
          </Box>
        )}

        <div className={classes.mapWrap}>
          {!gmapKey && (
            <Typography color="error">
              Missing `REACT_APP_GMAPKEY`. Map cannot render.
            </Typography>
          )}

          {!!gmapKey && (
            <LoadScript googleMapsApiKey={gmapKey}>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={11}
                onLoad={map => setMapRef(map)}
              >
                {routePath.length > 1 && (
                  <Polyline
                    path={routePath}
                    options={{
                      strokeColor: '#1E88E5',
                      strokeOpacity: 0.85,
                      strokeWeight: 4
                    }}
                  />
                )}

                {filteredCrewFeatures.map(feature => {
                  const [lng, lat] = feature?.geometry?.coordinates || []
                  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
                  const props = feature?.properties || {}
                  const markerColor = getCrewColor(props.crewColorIndex)

                  return (
                    <Marker
                      key={`crew-${props.crewId}`}
                      position={{ lat, lng }}
                      icon={window.google && window.google.maps
                        ? {
                            path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                            scale: 5,
                            fillColor: markerColor,
                            fillOpacity: 1,
                            strokeColor: '#111111',
                            strokeWeight: 1.5
                          }
                        : undefined}
                      label={{
                        text: 'C',
                        color: '#111111',
                        fontSize: '12px',
                        fontWeight: '700'
                      }}
                      title={`${props.crewName || 'Crew'} centroid`}
                    />
                  )
                })}

                {filteredPropertyFeatures.map(feature => {
                  const [lng, lat] = feature?.geometry?.coordinates || []
                  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

                  const props = feature?.properties || {}
                  const markerColor = getCrewColor(props.crewColorIndex)
                  const scale = props.isVip ? 9 : 7
                  const strokeColor = props.isLocked ? '#111111' : '#FFFFFF'
                  const strokeWeight = props.isLocked ? 2.2 : 1.2

                  return (
                    <Marker
                      key={`prop-${props.aspirePropertyId}`}
                      position={{ lat, lng }}
                      onClick={() => {
                        setSelectedProperty(feature)
                        setInfoWindowPropertyId(props.aspirePropertyId)
                      }}
                      icon={window.google && window.google.maps
                        ? {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale,
                            fillColor: markerColor,
                            fillOpacity: props.isAnchor ? 1 : 0.9,
                            strokeColor,
                            strokeWeight
                          }
                        : undefined}
                      title={props.propertyName || `Aspire #${props.aspirePropertyId}`}
                    />
                  )
                })}

                {selectedPropertyForInfoWindow && (() => {
                  const [lng, lat] = selectedPropertyForInfoWindow?.geometry?.coordinates || []
                  const props = selectedPropertyForInfoWindow?.properties || {}
                  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

                  return (
                    <InfoWindow
                      position={{ lat, lng }}
                      onCloseClick={() => setInfoWindowPropertyId(null)}
                    >
                      <div>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          {props.propertyName || `Aspire #${props.aspirePropertyId}`}
                        </Typography>
                        <Typography variant="caption" display="block">{props.assignedCrewName || 'Unassigned'}</Typography>
                        <Typography variant="caption" display="block">Frequency: {props.frequencyCode || 'Unspecified'}</Typography>
                        <Typography variant="caption" display="block">VIP: {props.isVip ? 'Yes' : 'No'} | Locked: {props.isLocked ? 'Yes' : 'No'}</Typography>
                      </div>
                    </InfoWindow>
                  )
                })()}
              </GoogleMap>
            </LoadScript>
          )}
        </div>

        <Paper variant="outlined" className={classes.detailPanel}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1">Property Detail Panel</Typography>
            <Typography variant="caption" color="textSecondary">
              Scenario snapshot: {useScenarioData ? formatDateTime(selectedScenario?.baseSnapshotAt) : 'Live'}
            </Typography>
          </Box>

          {!activeProperty && (
            <Typography variant="body2" color="textSecondary">
              Click a property pin to view details.
            </Typography>
          )}

          {!!activeProperty && (() => {
            const props = activeProperty.properties || {}
            const propertyCrewId = props.assignedCrewId ? String(props.assignedCrewId) : ''

            return (
              <>
                <div className={classes.detailGrid}>
                  <div>
                    <div className={classes.detailLabel}>Property</div>
                    <div className={classes.detailValue}>{props.propertyName || '-'}</div>
                  </div>
                  <div>
                    <div className={classes.detailLabel}>Aspire Property ID</div>
                    <div className={classes.detailValue}>{props.aspirePropertyId || '-'}</div>
                  </div>
                  <div>
                    <div className={classes.detailLabel}>Assigned Crew</div>
                    <div className={classes.detailValue}>{props.assignedCrewName || 'Unassigned'}</div>
                  </div>
                  <div>
                    <div className={classes.detailLabel}>Frequency</div>
                    <div className={classes.detailValue}>{props.frequencyCode || 'Unspecified'}</div>
                  </div>
                  <div>
                    <div className={classes.detailLabel}>Flags</div>
                    <div className={classes.detailValue}>
                      {props.isVip ? 'VIP ' : ''}
                      {props.isLocked ? 'LOCKED ' : ''}
                      {props.isAnchor ? 'ANCHOR' : ''}
                      {!props.isVip && !props.isLocked && !props.isAnchor ? '-' : ''}
                    </div>
                  </div>
                  <div>
                    <div className={classes.detailLabel}>Link Status</div>
                    <div className={classes.detailValue}>{props.linkStatus || '-'}</div>
                  </div>
                </div>

                <Box mt={1.5} display="flex" alignItems="center" gridGap={8}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      if (!propertyCrewId) return
                      setSelectedCrewId(propertyCrewId)
                    }}
                    disabled={!propertyCrewId}
                  >
                    Use Assigned Crew for Route
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => history.push(effectiveScenarioId ? `/routing/territory?scenarioId=${effectiveScenarioId}` : '/routing/territory')}
                  >
                    Open in Workbench
                  </Button>
                </Box>
              </>
            )
          })()}

          <div className={classes.markerLegend}>
            <Chip size="small" label="Circle pin: property" />
            <Chip size="small" label="Arrow pin (C): crew centroid" />
            <Chip size="small" label="Black border: locked" />
            <Chip size="small" label="Larger pin: VIP" />
          </div>
        </Paper>
      </Paper>

      {(loadingScenarios || loadingMap) && (
        <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">
            {loadingScenarios ? 'Loading scenarios...' : 'Loading map data...'}
          </Typography>
        </Box>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(RoutingTerritoryMapView)
